import { useContext, useEffect, useState } from "react";
import { getUsers } from "../utils/dataFetch";
import { dataContext, SERVER_HOST } from "../utils/constants";
import md5 from "../utils/md5";
import ConfirmModal from "./ConfirmModal";
import DropDownBtn from "./DropDownBtn";

const groupRightsMap = {
    grpSupply: ["catCounterparties_list", "docSupply_list", "docReject_list", "repSupplies", "repLiability"],
    grpStock: ["catGoods_list", "catDepartments_list", "docTransfer_list", "docExpence_list", "docSurplus_list", "docRevision_list", "repStock", "repForm496"],
    grpProd: ["irgStopList", "docProduction_list", "docConversion_list", "docCalculation_list", "repCalcs"],
    grpSales: ["catMenuGrp_list", "catDiscounts_list", "grpCerts", "catStreets_list", "docOrder_list", "repSales", "repReturn", "repDebt"],
    grpHR: ["catEmployees_list", "catPositions_list", "irgTimekeeping_list", "docSalary_list", "docPayment_list"],
    grpFinance: ["catArticles_list", "catCashAccs_list", "docStatement_list", "repCashflow", "docDebtadjust_list", "docCashadjust_list"],
    grpAdmin: ["catUsers_list", "catHalls_list", "catDevices_list", "catReasons_list", "catTexts_list", "servQRDesign", "repSysLog", "sysConst_list"],
    urCashTransactions: ["urCashOpp1", "urCashOpp2", "urCashOpp3", "urCashOpp4"],
};

export default function UsersEdit({ onBackToDashboard }) {
    const context = useContext(dataContext);
    const user = context.user;

    const [usersData, setUsersData] = useState([]);
    const [selectedUser, setSelectedUser] = useState({});
    const [newUser, setNewUser] = useState({ name: "", fio: "", password: "" });
    const [editableRights, setEditableRights] = useState(null);
    const [dataError, setDataError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const [showUrBack, setShowUrBack] = useState(false);
    const [showUrFront, setShowUrFront] = useState(false);
    const [showUrShef, setShowUrShef] = useState(false);
    const [showTemplates, setShowTemplates] = useState(false);

    const [isEdited, setIsEdited] = useState(false);
    const [showOnExitModal, setShowOnExitModal] = useState(false);
    const [templateName, setTemplateName] = useState("");
    const [showAddTemplateModal, setShowAddTemplateModal] = useState(false);
    const [modalDeleteConfig, setModalDeleteConfig] = useState({});
    const [showDeleteTemplateModal, setShowDeleteTemplateModal] = useState(false);
    const [showAddUserModal, setShowAddUserModal] = useState(false);
    const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);

    const [URTemplates, setURTemplates] = useState([]);

    const fetchUsers = async () => {
        try {
            const result = await getUsers();
            setUsersData(result);
        } catch (error) {
            setDataError("Помилка завантаження даних: " + error);
        }
    };

    const handelExit = (callback) => {
        if (isEdited) {
            setShowOnExitModal(true);
        } else {
            callback();
        }
    };

    const setUser = (id) => {
        if (id === "new") {
            setShowAddUserModal(true);
        } else {
            const user = usersData.find((u) => u.id === parseInt(id));
            setSelectedUser(user);
        }
    };

    const modUser = async (data) => {
        try {
            const response = await fetch(SERVER_HOST, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify({
                    action: "mod_user",
                    data: data,
                }),
            });

            const responseData = await response.json();
            if (responseData.status === "success") {
                setSuccessMessage("Операція успішна!");
                setTimeout(() => setSuccessMessage(""), 5000);
                setIsEdited(false);
            } else {
                setErrorMessage("Сталася помилка при додаванні: " + responseData.message);
                setTimeout(() => setErrorMessage(""), 10000);
            }
        } catch (e) {
            setErrorMessage("Сталася помилка при додаванні: " + e);
            setTimeout(() => setErrorMessage(""), 10000);
        }
    };

    const saveUser = async () => {
        try {
            const response = await fetch(SERVER_HOST, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify({
                    action: "add_user",
                    data: {
                        ...newUser,
                        password: md5(newUser.password),
                    },
                }),
            });

            const responseData = await response.json();
            if (responseData.status === "success") {
                setSuccessMessage("Операція успішна!");
                setTimeout(() => setSuccessMessage(""), 5000);
                setUsersData((prev) => [...prev, responseData.data]);
                setSelectedUser(responseData.data);
                setShowAddUserModal(false);
                setNewUser({ name: "", fio: "", password: "" });
                setEditableRights({
                    urFront: {},
                    urBack: {},
                    urShef: {},
                });
            } else {
                setErrorMessage("Сталася помилка при додаванні: " + responseData.message);
                setTimeout(() => setErrorMessage(""), 10000);
            }
        } catch (e) {
            setErrorMessage("Сталася помилка при додаванні: " + e);
            setTimeout(() => setErrorMessage(""), 10000);
        }
    };

    const deleteUser = async () => {
        try {
            const response = await fetch(SERVER_HOST, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify({
                    action: "rem_user",
                    data: {
                        id: selectedUser.id,
                    },
                }),
            });

            const responseData = await response.json();
            if (responseData.status === "success") {
                setSuccessMessage("Операція успішна!");
                setTimeout(() => setSuccessMessage(""), 5000);
                setUsersData((prev) => prev.filter((user) => user.id !== responseData.data.id));
                setSelectedUser(null);
                setEditableRights(null);
                setShowDeleteUserModal(false);
            } else {
                setErrorMessage("Сталася помилка при видаленні: " + responseData.message);
                setTimeout(() => setErrorMessage(""), 10000);
            }
        } catch (e) {
            setErrorMessage("Сталася помилка при видаленні: " + e);
            setTimeout(() => setErrorMessage(""), 10000);
        }
    };

    const saveUserRights = () => {
        const filteredData = {
            id: selectedUser.id,
            urAdmin: selectedUser.urAdmin,
        };

        for (const category in editableRights) {
            const group = editableRights[category];
            filteredData[category] = {};

            if (selectedUser[category] === null) {
                filteredData[category] = null;
            } else {
                for (const key in group) {
                    if (group[key] === 1) {
                        filteredData[category][key] = 1;
                    }
                }

                if (Object.keys(filteredData[category]).length === 0) {
                    filteredData[category] = null;
                }
            }
        }
        setIsEdited(true);
        modUser(filteredData);
    };

    const handleCheckboxChange = (category, key, value) => {
        setEditableRights((prev) => {
            const updatedCategory = { ...prev[category] };
            if (groupRightsMap[key]) {
                updatedCategory[key] = value;
                groupRightsMap[key].forEach((rightKey) => {
                    updatedCategory[rightKey] = value;
                });
            } else {
                updatedCategory[key] = value;
                const parentGroup = Object.keys(groupRightsMap).find((grp) => groupRightsMap[grp].includes(key));
                if (parentGroup) {
                    if (value === 1) {
                        updatedCategory[parentGroup] = 1;
                    } else {
                        const allZero = groupRightsMap[parentGroup].every((rightKey) => updatedCategory[rightKey] === 0);
                        if (allZero) {
                            updatedCategory[parentGroup] = 0;
                        }
                    }
                }
            }
            return {
                ...prev,
                [category]: updatedCategory,
            };
        });

        setIsEdited(true);
    };

    const setURTemplate = (template) => {
        const selected = URTemplates.find((item) => item.id === template.id);
        setSelectedUser((prev) => ({
            ...prev,
            urAdmin: parseInt(selected.value.urAdmin),
            urBack: selected.value.rightsBack,
            urFront: selected.value.rightsFront,
            urShef: selected.value.rightsShef,
        }));
        let erData = {};
        if (parseInt(selected.value.urBack)) erData = { urBack: { ...selected.value.rightsBack } };
        if (parseInt(selected.value.urFront)) erData = { ...erData, urFront: { ...selected.value.rightsFront } };
        if (parseInt(selected.value.urShef)) erData = { ...erData, urShef: { ...selected.value.rightsShef } };
        setEditableRights((prev) => ({ ...prev, ...erData }));
        setIsEdited(true);
    };

    const loadURTemplates = () => {
        const getURTemplates = async () => {
            try {
                const response = await fetch(SERVER_HOST, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: "include",
                    body: JSON.stringify({
                        action: "sys_tuning",
                        data: { type: 1 },
                    }),
                });
                const responseData = await response.json();
                if (responseData.status === "success") {
                    setURTemplates(responseData.data);
                } else {
                    setErrorMessage("Сталася помилка: " + responseData.message);
                    setTimeout(() => setErrorMessage(""), 10000);
                }
            } catch (e) {
                setErrorMessage("Сталася помилка: " + e);
                setTimeout(() => setErrorMessage(""), 10000);
            }
        };
        getURTemplates();
    };

    const saveURTemplate = (inputData) => {
        if (inputData === null) return;
        const saveTemplate = async (newData) => {
            try {
                const response = await fetch(SERVER_HOST, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: "include",
                    body: JSON.stringify({
                        action: "add_tuning",
                        data: newData,
                    }),
                });
                const responseData = await response.json();
                if (responseData.status === "success") {
                    setShowAddTemplateModal(false);
                    setSuccessMessage("Успішно додано " + responseData.data?.name);
                    setTimeout(() => setSuccessMessage(""), 5000);
                    loadURTemplates();
                } else {
                    setErrorMessage("Сталася помилка: " + responseData.message);
                    setTimeout(() => setErrorMessage(""), 10000);
                }
            } catch (e) {
                setErrorMessage("Сталася помилка: " + e);
                setTimeout(() => setErrorMessage(""), 10000);
            }
        };

        const filteredData = {};
        for (const category in editableRights) {
            const group = editableRights[category];
            filteredData[category] = {};
            if (selectedUser[category] === null) {
                filteredData[category] = null;
            } else {
                for (const key in group) {
                    if (group[key] === 1) {
                        filteredData[category][key] = 1;
                    }
                }
                if (Object.keys(filteredData[category]).length === 0) {
                    filteredData[category] = null;
                }
            }
        }
        let newData = {
            name: inputData,
            type: "1",
            userId: user.id,
            value: {
                urAdmin: selectedUser.urAdmin,
                urBack: selectedUser.urBack ? 1 : 0,
                urFront: selectedUser.urFront ? 1 : 0,
                urShef: selectedUser.urShef ? 1 : 0,
                rightsBack: filteredData.urBack,
                rightsFront: filteredData.urFront,
                rightsShef: filteredData.urShef,
            },
        };
        saveTemplate(newData);
    };

    const deleteURTemplate = (template) => {
        const deleteTemplate = async (id, name) => {
            try {
                const response = await fetch(SERVER_HOST, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: "include",
                    body: JSON.stringify({
                        action: "del_tuning",
                        data: { id, name },
                    }),
                });
                const responseData = await response.json();
                if (responseData.status === "success") {
                    setShowDeleteTemplateModal(false);
                    setSuccessMessage("Успішно видалено " + responseData.data?.name);
                    setTimeout(() => setSuccessMessage(""), 5000);
                    loadURTemplates();
                } else {
                    setErrorMessage("Сталася помилка: " + responseData.message);
                    setTimeout(() => setErrorMessage(""), 10000);
                }
            } catch (e) {
                setErrorMessage("Сталася помилка: " + e);
                setTimeout(() => setErrorMessage(""), 10000);
            }
        };
        deleteTemplate(template.id, template.name);
    };

    const deleteURTemplateClick = (template) => {
        setModalDeleteConfig({
            message: `Ви впевнені, що хочете видалити ${template.name}?`,
            onConfirm: () => deleteURTemplate(template),
        });
        setShowDeleteTemplateModal(true);
    };

    useEffect(() => {
        loadURTemplates();
        fetchUsers();
    }, []);

    useEffect(() => {
        if (selectedUser?.id) {
            const user = usersData.find((u) => u.id === parseInt(selectedUser.id));
            if (user) {
                setEditableRights({
                    urFront: { ...user.urFront },
                    urBack: { ...user.urBack },
                    urShef: { ...user.urShef },
                });
            }
        } else {
            setEditableRights(null);
        }
    }, [selectedUser?.id, usersData]);

    return (
        <div className="container dashboard--container pb-2" style={{ paddingTop: "40px" }}>
            <div className="row justify-content-around">
                {successMessage && (
                    <div className="alert alert-success alert--fixed rounded-5 d-flex justify-content-between align-items-center" style={{ marginTop: "40px" }}>
                        {successMessage}
                        <button type="button" className="btn btn-sm btn-close" aria-label="Close" onClick={() => setSuccessMessage("")}></button>
                    </div>
                )}
                {errorMessage && (
                    <div className="alert alert-danger alert--fixed rounded-5 d-flex justify-content-between align-items-center" style={{ marginTop: "40px" }}>
                        {errorMessage}
                        <button type="button" className="btn btn-sm btn-close" aria-label="Close" onClick={() => setErrorMessage("")}></button>
                    </div>
                )}
                <div className="col col-12 col-md-11 col-lg-9 col-xl-8 col-xxl-7 row justify-content-center m-0 p-0">
                    {dataError && <div className="alert alert-danger rounded-5">{dataError}</div>}

                    {!dataError && (
                        <>
                            <div className="col-12 col-md-11 col-lg-10 col-xl-9 col-xxl-8 col-md-auto dashboard--item flex-fill align-self-center">
                                <div className="dashboard--item--container p-3 h-100 d-flex flex-column align-items-center">
                                    <label htmlFor="userSelect" className="form-label fw-bold h5">
                                        Оберіть користувача:
                                    </label>
                                    <div className="d-flex w-100">
                                        <select
                                            id="userSelect"
                                            className="form-select form-select-sm rounded-5"
                                            value={selectedUser?.id || ""}
                                            onChange={(e) => setUser(e.target.value)}
                                        >
                                            {!selectedUser?.id && <option value="">-- Не вибрано --</option>}
                                            <option value="new" className="bg-primary text-white">
                                                ++ Додати користувача ++
                                            </option>
                                            {usersData.map((user) => (
                                                <option key={user.id} value={user.id}>
                                                    {user.name}
                                                </option>
                                            ))}
                                        </select>
                                        {selectedUser?.id && (
                                            <button className="btn btn-sm red--button ms-2 w-25" onClick={() => setShowDeleteUserModal(true)}>
                                                Видалити
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {selectedUser?.id && (
                                <>
                                    <div className="col-12 dashboard--item flex-fill align-self-center">
                                        <div className="dashboard--item--container p-3 h-100">
                                            <div className="col-12 row m-0 p-0">
                                                <div className="form-check m-0 col-6 col-md-3 mb-2 mb-md-0">
                                                    <input
                                                        className="form-check-input bg-secondary border-0"
                                                        type="checkbox"
                                                        id="urAdmin"
                                                        checked={selectedUser.urAdmin}
                                                        onChange={() => {
                                                            setSelectedUser({
                                                                ...selectedUser,
                                                                urAdmin: selectedUser.urAdmin === 1 ? 0 : 1,
                                                            });
                                                            setIsEdited(true);
                                                        }}
                                                    />
                                                    <label className="form-check-label" htmlFor="urAdmin">
                                                        Повні права
                                                    </label>
                                                </div>
                                                <div className="form-check m-0 col-6 col-md-3">
                                                    <input
                                                        className="form-check-input bg-secondary border-0"
                                                        type="checkbox"
                                                        id="urBack"
                                                        checked={selectedUser.urBack ? true : false}
                                                        onChange={() => {
                                                            setSelectedUser((prev) => ({
                                                                ...prev,
                                                                urBack: prev.urBack ? null : editableRights.urBack,
                                                            }));
                                                            setIsEdited(true);
                                                        }}
                                                    />
                                                    <label className="form-check-label" htmlFor="urBack">
                                                        Доступ до Back
                                                    </label>
                                                </div>
                                                <div className="form-check m-0 col-6 col-md-3 mb-2 mb-md-0">
                                                    <input
                                                        className="form-check-input bg-secondary border-0"
                                                        type="checkbox"
                                                        id="urFront"
                                                        checked={selectedUser.urFront ? true : false}
                                                        onChange={() => {
                                                            setSelectedUser((prev) => ({
                                                                ...prev,
                                                                urFront: prev.urFront ? null : editableRights.urFront,
                                                            }));
                                                            setIsEdited(true);
                                                        }}
                                                    />
                                                    <label className="form-check-label" htmlFor="urFront">
                                                        Доступ до Front
                                                    </label>
                                                </div>
                                                <div className="form-check m-0 col-6 col-md-3">
                                                    <input
                                                        className="form-check-input bg-secondary border-0"
                                                        type="checkbox"
                                                        id="urShef"
                                                        checked={selectedUser.urShef ? true : false}
                                                        onChange={() => {
                                                            setSelectedUser((prev) => ({
                                                                ...prev,
                                                                urShef: prev.urShef ? null : editableRights.urShef,
                                                            }));
                                                            setIsEdited(true);
                                                        }}
                                                    />
                                                    <label className="form-check-label" htmlFor="urShef">
                                                        Доступ до Shef
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {editableRights && (
                                <>
                                    {/* urBack */}
                                    {selectedUser.urBack && (
                                        <div
                                            className={`col-12 ${showUrBack && "col-md-auto"} dashboard--item flex-fill align-self-start ${
                                                (selectedUser.urFront || selectedUser.urShef) && showUrBack && "me-md-1"
                                            }`}
                                            style={{ minWidth: "45%" }}
                                        >
                                            <div className="dashboard--item--container p-3 h-100">
                                                <div className="position-relative">
                                                    <h5 className="text-center fw-bold">Права Back</h5>
                                                    <button
                                                        className="btn rounded-5 position-absolute top-0 end-0"
                                                        onClick={() => setShowUrBack((prev) => !prev)}
                                                        title={showUrBack ? "Сховати" : "Показати"}
                                                    >
                                                        {showUrBack ? <i className="bi bi-caret-up-fill"></i> : <i className="bi bi-caret-down-fill"></i>}
                                                    </button>
                                                </div>
                                                {showUrBack && (
                                                    <>
                                                        {/* grpSupply */}
                                                        <CBox
                                                            category="urBack"
                                                            keyName="grpSupply"
                                                            label="Закупівлі"
                                                            checked={editableRights.urBack.grpSupply === 1}
                                                            onChange={handleCheckboxChange}
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="catCounterparties_list"
                                                            label="Контрагенти"
                                                            checked={editableRights.urBack.catCounterparties_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="docSupply_list"
                                                            label="Поступлення"
                                                            checked={editableRights.urBack.docSupply_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="docReject_list"
                                                            label="Повернення"
                                                            checked={editableRights.urBack.docReject_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="repSupplies"
                                                            label="Звіт з поступлень"
                                                            checked={editableRights.urBack.repSupplies === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="repLiability"
                                                            label="Звіт по заборгованості"
                                                            checked={editableRights.urBack.repLiability === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        {/* grpStock */}
                                                        <CBox
                                                            category="urBack"
                                                            keyName="grpStock"
                                                            label="Склад"
                                                            checked={editableRights.urBack.grpStock === 1}
                                                            onChange={handleCheckboxChange}
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="catGoods_list"
                                                            label="Товари"
                                                            checked={editableRights.urBack.catGoods_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="catDepartments_list"
                                                            label="Підрозділи"
                                                            checked={editableRights.urBack.catDepartments_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="docTransfer_list"
                                                            label="Переміщення"
                                                            checked={editableRights.urBack.docTransfer_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="docExpence_list"
                                                            label="Списання"
                                                            checked={editableRights.urBack.docExpence_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="docSurplus_list"
                                                            label="Оприбуткування"
                                                            checked={editableRights.urBack.docSurplus_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="docRevision_list"
                                                            label="Інвентаризація"
                                                            checked={editableRights.urBack.docRevision_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="repStock"
                                                            label="Звіт про залишки"
                                                            checked={editableRights.urBack.repStock === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="repForm496"
                                                            label="Форма обліку товарних запасів"
                                                            checked={editableRights.urBack.repForm496 === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />

                                                        {/* grpProd */}
                                                        <CBox
                                                            category="urBack"
                                                            keyName="grpProd"
                                                            label="Виробництво"
                                                            checked={editableRights.urBack.grpProd === 1}
                                                            onChange={handleCheckboxChange}
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="irgStopList"
                                                            label="Стоп-лист"
                                                            checked={editableRights.urBack.irgStopList === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="docProduction_list"
                                                            label="Випуск"
                                                            checked={editableRights.urBack.docProduction_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="docConversion_list"
                                                            label="Переробка"
                                                            checked={editableRights.urBack.docConversion_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="docCalculation_list"
                                                            label="Калькуляції"
                                                            checked={editableRights.urBack.docCalculation_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="repCalcs"
                                                            label="Звіт по калькуляціях"
                                                            checked={editableRights.urBack.repCalcs === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />

                                                        {/* grpSales */}
                                                        <CBox
                                                            category="urBack"
                                                            keyName="grpSales"
                                                            label="Продажі"
                                                            checked={editableRights.urBack.grpSales === 1}
                                                            onChange={handleCheckboxChange}
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="catMenuGrp_list"
                                                            label="Категорії меню"
                                                            checked={editableRights.urBack.catMenuGrp_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="catDiscounts_list"
                                                            label="Клієнти/Знижки"
                                                            checked={editableRights.urBack.catDiscounts_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="grpCerts"
                                                            label="Сертифікати"
                                                            checked={editableRights.urBack.grpCerts === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="catStreets_list"
                                                            label="Адреси доставки"
                                                            checked={editableRights.urBack.catStreets_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="docOrder_list"
                                                            label="Журнал продаж"
                                                            checked={editableRights.urBack.docOrder_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="repSales"
                                                            label="Звіт з продаж"
                                                            checked={editableRights.urBack.repSales === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="repReturn"
                                                            label="Звіт по відмовах"
                                                            checked={editableRights.urBack.repReturn === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="repDebt"
                                                            label="Звіт по боржниках"
                                                            checked={editableRights.urBack.repDebt === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />

                                                        {/* grpHR */}
                                                        <CBox
                                                            category="urBack"
                                                            keyName="grpHR"
                                                            label="Персонал"
                                                            checked={editableRights.urBack.grpHR === 1}
                                                            onChange={handleCheckboxChange}
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="catEmployees_list"
                                                            label="Працівники"
                                                            checked={editableRights.urBack.catEmployees_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="catPositions_list"
                                                            label="Посади"
                                                            checked={editableRights.urBack.catPositions_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="irgTimekeeping_list"
                                                            label="Табелі"
                                                            checked={editableRights.urBack.irgTimekeeping_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="docSalary_list"
                                                            label="Зарплата"
                                                            checked={editableRights.urBack.docSalary_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="docPayment_list"
                                                            label="Виплати з/п"
                                                            checked={editableRights.urBack.docPayment_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />

                                                        {/* grpFinance */}
                                                        <CBox
                                                            category="urBack"
                                                            keyName="grpFinance"
                                                            label="Фінанси"
                                                            checked={editableRights.urBack.grpFinance === 1}
                                                            onChange={handleCheckboxChange}
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="catArticles_list"
                                                            label="Статті руху"
                                                            checked={editableRights.urBack.catArticles_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="catCashAccs_list"
                                                            label="Каси/Рахунки"
                                                            checked={editableRights.urBack.catCashAccs_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="docStatement_list"
                                                            label="Касова виписка"
                                                            checked={editableRights.urBack.docStatement_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="repCashflow"
                                                            label="Звіт про рух коштів"
                                                            checked={editableRights.urBack.repCashflow === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="docDebtadjust_list"
                                                            label="Коригування боргів"
                                                            checked={editableRights.urBack.docDebtadjust_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="docCashadjust_list"
                                                            label="Коригування коштів"
                                                            checked={editableRights.urBack.docCashadjust_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />

                                                        {/* grpAdmin */}
                                                        <CBox
                                                            category="urBack"
                                                            keyName="grpAdmin"
                                                            label="Адміністрування"
                                                            checked={editableRights.urBack.grpAdmin === 1}
                                                            onChange={handleCheckboxChange}
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="catUsers_list"
                                                            label="Користувачі"
                                                            checked={editableRights.urBack.catUsers_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="catHalls_list"
                                                            label="Зали"
                                                            checked={editableRights.urBack.catHalls_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="catDevices_list"
                                                            label="Обладнання"
                                                            checked={editableRights.urBack.catDevices_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="catReasons_list"
                                                            label="Шаблони відмов"
                                                            checked={editableRights.urBack.catReasons_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="catTexts_list"
                                                            label="Шаблони коментарів"
                                                            checked={editableRights.urBack.catTexts_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="servQRDesign"
                                                            label="QR коди"
                                                            checked={editableRights.urBack.servQRDesign === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="repSysLog"
                                                            label="Історія дій"
                                                            checked={editableRights.urBack.repSysLog === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                        <CBox
                                                            category="urBack"
                                                            keyName="sysConst_list"
                                                            label="Налаштування"
                                                            checked={editableRights.urBack.sysConst_list === 1}
                                                            onChange={handleCheckboxChange}
                                                            marginLeft="3"
                                                        />
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {(selectedUser.urFront || selectedUser.urShef) && (
                                        <div
                                            className={`col-12 col-md-auto m-0 p-0 flex-fill ${(!selectedUser.urBack || !showUrBack) && showUrFront && "d-md-flex"} ${
                                                selectedUser.urBack && showUrBack && "ms-md-1"
                                            }`}
                                        >
                                            {/* urFront */}
                                            {selectedUser.urFront && (
                                                <div
                                                    className={`dashboard--item flex-fill align-self-start ${
                                                        (!selectedUser.urBack || !showUrBack) && selectedUser.urShef && showUrFront && "me-md-1"
                                                    }`}
                                                    style={{ minWidth: "45%" }}
                                                >
                                                    <div className="dashboard--item--container p-3 h-100">
                                                        <div className="position-relative">
                                                            <h5 className="text-center fw-bold">Права Front</h5>
                                                            <button
                                                                className="btn rounded-5 position-absolute top-0 end-0"
                                                                onClick={() => setShowUrFront((prev) => !prev)}
                                                                title={showUrFront ? "Сховати" : "Показати"}
                                                            >
                                                                {showUrFront ? <i className="bi bi-caret-up-fill"></i> : <i className="bi bi-caret-down-fill"></i>}
                                                            </button>
                                                        </div>
                                                        {showUrFront && (
                                                            <>
                                                                <CBox
                                                                    category="urFront"
                                                                    keyName="urInner_work"
                                                                    label="Робота з залами в закладі"
                                                                    checked={editableRights?.urFront?.urInner_work === 1}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                <CBox
                                                                    category="urFront"
                                                                    keyName="urDelivery"
                                                                    label="Робота на доставку"
                                                                    checked={editableRights?.urFront?.urDelivery === 1}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                <CBox
                                                                    category="urFront"
                                                                    keyName="urPickup"
                                                                    label="Робота на самовивіз"
                                                                    checked={editableRights?.urFront?.urPickup === 1}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                <CBox
                                                                    category="urFront"
                                                                    keyName="urHall"
                                                                    label="Вибирати зали"
                                                                    checked={editableRights?.urFront?.urHall === 1}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                <CBox
                                                                    category="urFront"
                                                                    keyName="urDiscount"
                                                                    label="Давати знижки"
                                                                    checked={editableRights?.urFront?.urDiscount === 1}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                <CBox
                                                                    category="urFront"
                                                                    keyName="urDiscSum"
                                                                    label="Давати знижки сумою"
                                                                    checked={editableRights?.urFront?.urDiscSum === 1}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                <CBox
                                                                    category="urFront"
                                                                    keyName="urReport"
                                                                    label="Формувати загальні звіти"
                                                                    checked={editableRights?.urFront?.urReport === 1}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                <CBox
                                                                    category="urFront"
                                                                    keyName="urTable"
                                                                    label="Вибирати чужі столи"
                                                                    checked={editableRights?.urFront?.urTable === 1}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                <CBox
                                                                    category="urFront"
                                                                    keyName="urReject"
                                                                    label="Робити відмову"
                                                                    checked={editableRights?.urFront?.urReject === 1}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                <CBox
                                                                    category="urFront"
                                                                    keyName="urBreack"
                                                                    label="Розділяти замовлення"
                                                                    checked={editableRights?.urFront?.urBreack === 1}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                <CBox
                                                                    category="urFront"
                                                                    keyName="urClose"
                                                                    label="Закривати замовлення"
                                                                    checked={editableRights?.urFront?.urClose === 1}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                <CBox
                                                                    category="urFront"
                                                                    keyName="urUnclose"
                                                                    label="Відміняти закриття замовлення"
                                                                    checked={editableRights?.urFront?.urUnclose === 1}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                <CBox
                                                                    category="urFront"
                                                                    keyName="urOrder"
                                                                    label="Відкривати чужі замовлення"
                                                                    checked={editableRights?.urFront?.urOrder === 1}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                <CBox
                                                                    category="urFront"
                                                                    keyName="urNSedit"
                                                                    label="Редагувати чужі замовлення"
                                                                    checked={editableRights?.urFront?.urNSedit === 1}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                <CBox
                                                                    category="urFront"
                                                                    keyName="urNSclose"
                                                                    label="Закривати чужі замовлення"
                                                                    checked={editableRights?.urFront?.urNSclose === 1}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                <CBox
                                                                    category="urFront"
                                                                    keyName="urFiscal"
                                                                    label="Керувати фіск. апаратом"
                                                                    checked={editableRights?.urFront?.urFiscal === 1}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                {/* grp urCashTransactions */}
                                                                <CBox
                                                                    category="urFront"
                                                                    keyName="urCashTransactions"
                                                                    label="Операції з касою"
                                                                    checked={editableRights?.urFront?.urCashTransactions === 1}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                <CBox
                                                                    category="urFront"
                                                                    keyName="urCashOpp1"
                                                                    label="Передача до іншої каси"
                                                                    checked={editableRights?.urFront?.urCashOpp1 === 1}
                                                                    onChange={handleCheckboxChange}
                                                                    marginLeft="3"
                                                                />
                                                                <CBox
                                                                    category="urFront"
                                                                    keyName="urCashOpp2"
                                                                    label="Оплата постачальнику"
                                                                    checked={editableRights?.urFront?.urCashOpp2 === 1}
                                                                    onChange={handleCheckboxChange}
                                                                    marginLeft="3"
                                                                />
                                                                <CBox
                                                                    category="urFront"
                                                                    keyName="urCashOpp3"
                                                                    label="Інші видатки"
                                                                    checked={editableRights?.urFront?.urCashOpp3 === 1}
                                                                    onChange={handleCheckboxChange}
                                                                    marginLeft="3"
                                                                />
                                                                <CBox
                                                                    category="urFront"
                                                                    keyName="urCashOpp4"
                                                                    label="Інші поступлення"
                                                                    checked={editableRights?.urFront?.urCashOpp4 === 1}
                                                                    onChange={handleCheckboxChange}
                                                                    marginLeft="3"
                                                                />
                                                                <CBox
                                                                    category="urFront"
                                                                    keyName="urGetPush"
                                                                    label="Сповіщення про онлайн замовлення"
                                                                    checked={editableRights?.urFront?.urGetPush === 1}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                            {/* urShef */}
                                            {selectedUser.urShef && (
                                                <div
                                                    className={`dashboard--item flex-fill align-self-start ${
                                                        (!selectedUser.urBack || !showUrBack) && selectedUser.urFront && showUrFront && "ms-md-1"
                                                    }`}
                                                    style={{ minWidth: "45%" }}
                                                >
                                                    <div className="dashboard--item--container p-3 h-100">
                                                        <div className="position-relative">
                                                            <h5 className="text-center fw-bold">Права Shef</h5>
                                                            <button
                                                                className="btn rounded-5 position-absolute top-0 end-0"
                                                                onClick={() => setShowUrShef((prev) => !prev)}
                                                                title={showUrShef ? "Сховати" : "Показати"}
                                                            >
                                                                {showUrShef ? <i className="bi bi-caret-up-fill"></i> : <i className="bi bi-caret-down-fill"></i>}
                                                            </button>
                                                        </div>
                                                        {showUrShef && (
                                                            <>
                                                                <CBox
                                                                    category="urShef"
                                                                    keyName="urSalesReport"
                                                                    label="Статистика продажів"
                                                                    checked={editableRights?.urShef?.urSalesReport === 1}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                <CBox
                                                                    category="urShef"
                                                                    keyName="urFiscalReport"
                                                                    label="Офіційні продажі"
                                                                    checked={editableRights?.urShef?.urFiscalReport === 1}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                <CBox
                                                                    category="urShef"
                                                                    keyName="urEfficiencyStatistics"
                                                                    label="Статистика ефективності"
                                                                    checked={editableRights?.urShef?.urEfficiencyStatistics === 1}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                <CBox
                                                                    category="urShef"
                                                                    keyName="urCashBalances"
                                                                    label="Залишки коштів"
                                                                    checked={editableRights?.urShef?.urCashBalances === 1}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                <CBox
                                                                    category="urShef"
                                                                    keyName="urSuppliersDebt"
                                                                    label="Борг постачальникам"
                                                                    checked={editableRights?.urShef?.urSuppliersDebt === 1}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                <CBox
                                                                    category="urShef"
                                                                    keyName="urPaymentTypes"
                                                                    label="Види оплати"
                                                                    checked={editableRights?.urShef?.urPaymentTypes === 1}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                <CBox
                                                                    category="urShef"
                                                                    keyName="urPeakHours"
                                                                    label="Пікові години"
                                                                    checked={editableRights?.urShef?.urPeakHours === 1}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                <CBox
                                                                    category="urShef"
                                                                    keyName="urPopularMenu"
                                                                    label="Популярні страви"
                                                                    checked={editableRights?.urShef?.urPopularMenu === 1}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                <CBox
                                                                    category="urShef"
                                                                    keyName="urStopList"
                                                                    label="Стоп-лист"
                                                                    checked={editableRights?.urShef?.urStopList === 1}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                <CBox
                                                                    category="urShef"
                                                                    keyName="urLossSoldGoods"
                                                                    label="Продажі в збиток"
                                                                    checked={editableRights?.urShef?.urLossSoldGoods === 1}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}

                            {selectedUser?.id && (
                                <div className="col-12 dashboard--item flex-fill align-self-center">
                                    <div className="dashboard--item--container p-3 h-100">
                                        <div className="position-relative">
                                            <h5 className="text-center fw-bold">Скористатись шаблоном</h5>
                                            <button
                                                className="btn rounded-5 position-absolute top-0 end-0"
                                                onClick={() => setShowTemplates((prev) => !prev)}
                                                title={showTemplates ? "Сховати" : "Показати"}
                                            >
                                                {showTemplates ? <i className="bi bi-caret-up-fill"></i> : <i className="bi bi-caret-down-fill"></i>}
                                            </button>
                                        </div>
                                        {showTemplates && (
                                            <div className="col-12 m-0 p-0 d-flex flex-column justify-content-around align-items-center">
                                                <div className="m-1 p-0">
                                                    <DropDownBtn
                                                        data={URTemplates}
                                                        caption={"Заповнити з шаблону"}
                                                        onSelect={setURTemplate}
                                                        id="URTemplatesChoose"
                                                        btnStyles={{ width: "240px" }}
                                                    />
                                                </div>
                                                <div className="m-1 p-0">
                                                    <button className="btn btn-sm btn-dark rounded-5" style={{ width: "240px" }} onClick={() => setShowAddTemplateModal(true)}>
                                                        Збереги як шаблон
                                                    </button>
                                                </div>
                                                <div className="m-1 p-0">
                                                    <DropDownBtn
                                                        data={URTemplates}
                                                        caption={"Видалити шаблон"}
                                                        onSelect={deleteURTemplateClick}
                                                        id="URTemplatesDel"
                                                        btnClasses="red--button btn-sm"
                                                        btnStyles={{ width: "240px" }}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="col-12 row justify-content-around">
                                <div className="col-10 col-sm-7 col-md-6 col-lg-5 mt-2 mb-1 d-flex justify-content-around">
                                    <button className="btn btn-sm btn-dark rounded-5 me-1 me-sm-0" style={{ width: "130px" }} onClick={() => handelExit(onBackToDashboard)}>
                                        На головну
                                    </button>
                                    {selectedUser?.id && (
                                        <button className="btn btn-sm main--button ms-1 ms-sm-0" style={{ width: "130px" }} onClick={saveUserRights}>
                                            Зберегти
                                        </button>
                                    )}
                                </div>
                            </div>

                            {showOnExitModal && (
                                <ConfirmModal
                                    actions={[
                                        { label: "Скасувати", onClick: () => setShowOnExitModal(false), className: "btn-dark" },
                                        { label: "OK", onClick: onBackToDashboard, className: "main--button ps-4 pe-4" },
                                    ]}
                                >
                                    <p className="m-0">Дані не збережено! Повернутися на "Головну" сторінку без збереження?</p>
                                </ConfirmModal>
                            )}

                            {showDeleteTemplateModal && (
                                <ConfirmModal
                                    title="Видалення"
                                    actions={[
                                        { label: "Скасувати", onClick: () => setShowDeleteTemplateModal(false), className: "btn-dark" },
                                        { label: "Видалити", onClick: modalDeleteConfig.onConfirm, className: "btn-danger ps-4 pe-4" },
                                    ]}
                                >
                                    <p className="m-0">{modalDeleteConfig.message}</p>
                                </ConfirmModal>
                            )}

                            {showAddTemplateModal && (
                                <ConfirmModal
                                    title="Новий шаблон"
                                    actions={[
                                        { label: "Скасувати", onClick: () => setShowAddTemplateModal(false), className: "btn-dark" },
                                        { label: "Зберегти", onClick: () => saveURTemplate(templateName), className: "main--button ps-4 pe-4" },
                                    ]}
                                >
                                    <>
                                        <p className="m-0">Введіть назву шаблону</p>
                                        <input
                                            type="text"
                                            className="w-100 form-control rounded-5 mt-2"
                                            value={templateName}
                                            onChange={(e) => setTemplateName(e.target.value)}
                                            required
                                        />
                                    </>
                                </ConfirmModal>
                            )}

                            {showAddUserModal && (
                                <ConfirmModal
                                    title="Новий користувач"
                                    actions={[
                                        { label: "Скасувати", onClick: () => setShowAddUserModal(false), className: "btn-dark" },
                                        { label: "Зберегти", onClick: () => saveUser(), className: "main--button ps-4 pe-4" },
                                    ]}
                                >
                                    <>
                                        <label className="ms-3" htmlFor="fio">ПІБ для друку:</label>
                                        <input
                                            id="fio"
                                            type="text"
                                            className="w-100 form-control rounded-5"
                                            value={newUser.fio || ""}
                                            onChange={(e) => setNewUser({ ...newUser, fio: e.target.value })}
                                            required
                                        />
                                        <label className="ms-3 mt-2" htmlFor="name">Ім'я:</label>
                                        <input
                                            id="name"
                                            type="text"
                                            className="w-100 form-control rounded-5"
                                            value={newUser.name || ""}
                                            onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                                            required
                                        />
                                        <label className="ms-3 mt-2" htmlFor="password">Пароль:</label>
                                        <input
                                            id="password"
                                            type="password"
                                            className="w-100 form-control rounded-5"
                                            value={newUser.password || ""}
                                            onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                                            required
                                        />
                                    </>
                                </ConfirmModal>
                            )}
                            {showDeleteUserModal && (
                                <ConfirmModal
                                    title="Видалення"
                                    actions={[
                                        { label: "Скасувати", onClick: () => setShowDeleteUserModal(false), className: "btn-dark" },
                                        { label: "Так", onClick: () => deleteUser(), className: "main--button ps-4 pe-4" },
                                    ]}
                                >
                                    <p className="m-0">Ви впевнені що хочете видалити користувача "{selectedUser.name}?"</p>
                                </ConfirmModal>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function CBox({ category, keyName, label, checked, onChange, marginLeft = "0" }) {
    return (
        <div className="col-11 mb-2">
            <div className={`form-check ms-${marginLeft}`}>
                <input
                    className="form-check-input bg-secondary border-0"
                    type="checkbox"
                    id={`${category}-${keyName}`}
                    checked={checked}
                    onChange={() => onChange(category, keyName, checked ? 0 : 1)}
                />
                <label className="form-check-label" htmlFor={`${category}-${keyName}`}>
                    {label || keyName}
                </label>
            </div>
        </div>
    );
}
